import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { MonthRange } from '@/components/MonthControls';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { productsApi, reportsApi } from '@/lib/api/endpoints';
import { fmtDay, fmtInt, fmtMoney } from '@/lib/format';
import { cn } from '@/lib/utils';

export function ProductsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const months = useQuery({ queryKey: ['months'], queryFn: reportsApi.months, staleTime: 60_000 });
  const available = months.data ?? [];
  const from = params.get('from') ?? available[0] ?? '';
  const to = params.get('to') ?? available[available.length - 1] ?? '';
  const search = params.get('q') ?? '';
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  const set = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  const q = useQuery({
    queryKey: ['products', from, to, search],
    queryFn: () =>
      productsApi.list({
        from: from || undefined,
        to: to || undefined,
        search: search || undefined,
      }),
    enabled: !months.isLoading,
    placeholderData: (p) => p,
  });
  const totalNet = q.data?.reduce((acc, p) => acc + p.totals.netSales, 0) ?? 0;

  return (
    <div>
      <PageHeader
        title="Produtos"
        description="A coluna Account do relatório: faturamento de cada produto no período e quantos afiliados venderam"
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <MonthRange
          months={available}
          from={from}
          to={to}
          onChange={(f, t) => set({ from: f, to: t })}
        />
        <div className="min-w-56 flex-1 space-y-1">
          <Label className="text-xs" htmlFor="products-q">
            Buscar
          </Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <Input
              id="products-q"
              placeholder="Nome do produto"
              className="pl-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && set({ q: searchInput.trim() })}
              onBlur={() => searchInput.trim() !== search && set({ q: searchInput.trim() })}
            />
          </div>
        </div>
      </div>
      <div className="rounded-lg border bg-card">
        {q.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : q.data?.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Produto</TableHead>
                <TableHead className="text-right">Bruto</TableHead>
                <TableHead className="text-right">Líquido</TableHead>
                <TableHead className="text-right">% líq.</TableHead>
                <TableHead className="text-right">Comissões</TableHead>
                <TableHead className="text-right">Pedidos</TableHead>
                <TableHead className="text-right">Afiliados c/ venda</TableHead>
                <TableHead>Período</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.data.map((p) => (
                <TableRow
                  key={p.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/mensal?by=affiliate&from=${from}&to=${to}&q=`)}
                  title="Ver afiliados deste produto no Planilhão"
                >
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtMoney(p.totals.grossSales)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-right font-medium tabular-nums',
                      p.totals.netSales < 0 && 'text-destructive',
                    )}
                  >
                    {fmtMoney(p.totals.netSales)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {totalNet > 0
                      ? `${((p.totals.netSales / totalNet) * 100).toFixed(1).replace('.', ',')}%`
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtMoney(p.totals.commissionsNet)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtInt(p.totals.orders)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtInt(p.affiliatesActive)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {p.firstPeriod ? `${fmtDay(p.firstPeriod)} – ${fmtDay(p.lastPeriod)}` : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={Package}
              title="Nenhum produto"
              description={
                search ? 'Tente outro termo.' : 'Importe relatórios para ver os produtos.'
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
